import {
    debounce, find, difference, isEmpty, isArray,
} from 'lodash';
import { normalize } from 'normalizr';
import { CONSTANTS } from 'services/constants';
import { deleteFromDatabase, putDatabase } from 'store/actions/database.actions';

import * as Q from 'q';
import { defaultCursor } from 'utils/sorting-methods';

const queue = [];

export function buildRepositoryAction(repository, action, args)
{
    return { repository, action, args };
}

export function containsIncludes(newIncludes, currentIncludes)
{
    let includes = newIncludes;

    if (isEmpty(includes))
    {
        return true;
    }

    if (isEmpty(currentIncludes))
    {
        return false;
    }

    if (!isArray(newIncludes))
    {
        includes = [includes];
    }

    return includes
        .filter((i) => !currentIncludes.includes(i))
        .length === 0;
}

export function loadEntity(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return async (dispatch, getState) => loadEntityAction(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema, dispatch, getState);
}

export function initializeMetaEntity(id, actionCreators)
{
    return async (dispatch) =>
    {
        dispatch(actionCreators._loading(id, false));
    };
}

export function loadAll(queryBuilder, includes, metaSelectorBuilder, actionCreators, repository, entitySchema, filters)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = metaSelectorBuilder();
        const metaEntity = metaEntitySelector(getState(), CONSTANTS.DATA.ALL_ID);

        if (metaEntity.loading) return;

        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, true, includes));
        dispatch(actionCreators._filters(CONSTANTS.DATA.ALL_ID, filters));

        const r = await repository.all({ ...queryBuilder, includes });
        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(CONSTANTS.DATA.ALL_ID, r.mergeMessages()));
            return r; // eslint-disable-line consistent-return
        }

        const n = normalize(r.response.data, [entitySchema]);
        const meta = r.response?.meta?.cursor ?? defaultCursor;

        dispatch(actionCreators._paginate(CONSTANTS.DATA.ALL_ID, meta, filters, n.result));
        dispatch(putDatabase(n.entities));
        dispatch(actionCreators._loading(CONSTANTS.DATA.ALL_ID, false));
        dispatch(actionCreators._setLoadedIncludes(CONSTANTS.DATA.ALL_ID, includes));

        return r; // eslint-disable-line consistent-return
    };
}

export function saveEntity(id, actionCreators, repositoryData, entitySchema)
{
    return async (dispatch) =>
    {
        dispatch(actionCreators._loading(id || CONSTANTS.DATA.NEW_ID, true));
        const r = await repositoryData.repository[repositoryData.action](...repositoryData.args);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id || CONSTANTS.DATA.NEW_ID, r.mergeMessages()));
        }
        else
        {
            const n = normalize(r.response, entitySchema);
            dispatch(putDatabase(n.entities));

            dispatch(actionCreators._loading(id || CONSTANTS.DATA.NEW_ID, false));
        }

        return r;
    };
}

export function saveEntities(ids, actionCreators, repositoryData, entitySchema)
{
    return async (dispatch) =>
    {
        dispatch(actionCreators._batchLoading(ids || [CONSTANTS.DATA.NEW_ID], true));

        const r = await repositoryData.repository[repositoryData.action](...repositoryData.args);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._batchError(ids || [CONSTANTS.DATA.NEW_ID], r.mergeMessages()));
        }
        else
        {
            const n = normalize(r.response, entitySchema);
            dispatch(putDatabase(n.entities));

            dispatch(actionCreators._batchLoading(ids || [CONSTANTS.DATA.NEW_ID], false));
        }

        return r;
    };
}

export function deleteEntity(id, includes, actionCreators, repository, entitySchema)
{
    return async (dispatch) =>
    {
        dispatch(actionCreators._loading(id, true));

        const r = await repository.destroy(id, includes);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
        }

        else
        {
            dispatch(deleteFromDatabase(id, entitySchema.key));

            dispatch(actionCreators._loading(id, false));
        }

        return r;
    };
}

export function loadEntityWithQueue(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema)
{
    return (dispatch, getState) => _addToQueue(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema, dispatch, getState);
}

async function loadEntityAction(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema, dispatch, getState)
{
    const metaEntitySelector = metaSelectorBuilder();
    const metaEntity = metaEntitySelector(getState(), id);

    if (!metaEntity.needUpdate && metaEntity.loading && containsIncludes(includes, metaEntity.includes))
    {
        return null;
    }

    dispatch(actionCreators._needUpdate(id, false));
    dispatch(actionCreators._loading(id, true, includes));

    const r = await repository.find(id, includes);

    if (!r.isSuccess())
    {
        dispatch(actionCreators._error(id, r.mergeMessages()));

        return r;
    }

    const n = normalize(r.response, entitySchema);
    dispatch(putDatabase(n.entities));

    dispatch(actionCreators._setLoadedIncludes(id, includes));

    dispatch(actionCreators._loading(id, false));

    return r;
}

function _addToQueue(id, includes, metaSelectorBuilder, actionCreators, repository, entitySchema, dispatch, getState)
{
    const deferred = Q.defer();

    const key = entitySchema.key + id;

    const item = find(queue, { key });

    if (item)
    {
        const missingIncludes = difference(includes, item.includes);

        item.includes = [...item.includes, ...missingIncludes];
        item.defers.push(deferred);
    }
    else
    {
        queue.push({
            args: {
                metaSelectorBuilder,
                actionCreators,
                repository,
                entitySchema,
                dispatch,
                getState,
            },
            defers: [deferred],
            key,
            id,
            includes,
        });
    }

    _processQueue();

    return deferred.promise;
}

const _processQueue = debounce(() =>
{
    const item = queue.shift();

    const result = loadEntityAction(item.id, item.includes, item.args.metaSelectorBuilder, item.args.actionCreators, item.args.repository, item.args.entitySchema, item.args.dispatch, item.args.getState);

    item.defers.forEach((deferred) =>
    {
        deferred.resolve(result);
    });

    if (queue.length)
    {
        _processQueue();
    }
}, 500);
